import type { TimelineItem, TimelineTrack } from '@/types/timeline';

export type CtaSnapshot = {
  brandSnapshot?: {
    name?: string;
    channelName?: string;
    channelHandle?: string;
    logoUrl?: string;
    backgroundImageUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    defaultHeadline?: string;
    defaultSubtext?: string;
    defaultButtonLabel?: string;
  };
  presetSnapshot?: {
    name?: string;
    headline?: string;
    subtext?: string;
    buttonLabel?: string;
    durationSec?: number;
    ttsDurationSec?: number;
    renderMode?: 'scene' | 'overlay';
    placement?: 'middle' | 'end' | 'both';
    backgroundStyle?: 'solid' | 'gradient' | 'image';
    backgroundColor?: string;
    backgroundColorSecondary?: string;
    backgroundImageUrl?: string;
    ttsAudioUrl?: string;
  };
  placement?: 'middle' | 'end' | 'both' | 'after_scene';
  insertAfterSceneId?: string;
  renderMode?: 'scene' | 'overlay';
  overrideHeadline?: string;
  overrideSubtext?: string;
  overrideButtonLabel?: string;
  showLogo?: boolean;
  showHandle?: boolean;
  applyBranding?: boolean;
  enabled?: boolean;
};

type AssetEntry = {
  id: string;
  fileName?: string;
  mimeType?: string;
  type?: string;
};

type InsertArgs = {
  tracks: TimelineTrack[];
  items: TimelineItem[];
  assets: AssetEntry[];
  ctaConfig?: CtaSnapshot;
  width: number;
  height: number;
  fps: number;
};

const CTA_BG_TRACK_ID = 'cta-background-track';
const CTA_TEXT_TRACK_ID = 'cta-text-track';
const CTA_AUDIO_TRACK_ID = 'audio-track';
const CTA_ORIGIN_PREFIX = 'cta-block';
const SHORTS_BASE_WIDTH = 1080;
const SHORTS_BASE_HEIGHT = 1920;
const LONG_BASE_WIDTH = 1920;
const LONG_BASE_HEIGHT = 1080;

const isRenderableRemoteUrl = (value?: string) =>
  !!value && /^(https?:\/\/|\/api\/|\/v1\/|blob:|data:)/i.test(value);

const getFileName = (url: string, fallback: string) => {
  try {
    return url.split('/').pop()?.split('?')[0] || fallback;
  } catch {
    return fallback;
  }
};

const ensureTrack = (
  tracks: TimelineTrack[],
  trackId: string,
  name: string,
  order: number
) => {
  let track = tracks.find((item) => item.id === trackId);
  if (!track) {
    track = {
      id: trackId,
      name,
      order,
      height: 32,
      locked: false,
      visible: true,
      muted: false,
      solo: false,
      items: [],
    };
    tracks.push(track);
  }
  return track;
};

const ensureAsset = (assets: AssetEntry[], url: string, type: 'image' | 'audio') => {
  if (!url || assets.some((asset) => asset.id === url)) return;
  const fileName = getFileName(url, type === 'audio' ? 'cta.wav' : 'cta.png');
  assets.push({
    id: url,
    fileName,
    mimeType: type === 'audio'
      ? (fileName.endsWith('.wav') ? 'audio/wav' : 'audio/mpeg')
      : (fileName.endsWith('.png') ? 'image/png' : 'image/jpeg'),
    type,
  });
};

const makeId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`;

const scaleTemplateRect = (
  targetWidth: number,
  targetHeight: number,
  baseWidth: number,
  baseHeight: number,
  rect: { x: number; y: number; width: number; height: number }
) => ({
  x: Math.round((rect.x / baseWidth) * targetWidth),
  y: Math.round((rect.y / baseHeight) * targetHeight),
  width: Math.round((rect.width / baseWidth) * targetWidth),
  height: Math.round((rect.height / baseHeight) * targetHeight),
});

const addTrackItem = (tracks: TimelineTrack[], item: TimelineItem) => {
  const track = tracks.find((entry) => entry.id === item.trackId);
  track?.items.push(item);
};

const syncTrackItems = (tracks: TimelineTrack[], items: TimelineItem[]) =>
  tracks.map((track) => ({
    ...track,
    items: items
      .filter((item) => item.trackId === track.id)
      .sort((left, right) => left.from - right.from),
  }));

const shiftItemsFromFrame = (items: TimelineItem[], fromFrame: number, deltaFrames: number) =>
  items.map((item) => {
    if (isCtaItem(item)) return item;
    if (item.from >= fromFrame) {
      return {
        ...item,
        from: item.from + deltaFrames,
      };
    }
    return item;
  });

const findMidInsertFrame = (items: TimelineItem[], totalFrames: number) => {
  const midpoint = totalFrames / 2;
  const sceneLikeItems = items
    .filter((item) => !isCtaItem(item))
    .filter((item) => item.type === 'video' || item.type === 'image' || item.type === 'composition')
    .filter((item) => item.durationInFrames >= 12)
    .sort((left, right) => left.from - right.from);

  const boundaryCandidates = sceneLikeItems.flatMap((item) => [
    item.from,
    item.from + item.durationInFrames,
  ]);

  if (boundaryCandidates.length === 0) {
    return Math.max(0, Math.round(midpoint));
  }

  const laterBoundary = boundaryCandidates
    .filter((frame) => frame >= midpoint)
    .sort((left, right) => left - right)[0];

  if (laterBoundary !== undefined) return laterBoundary;

  return boundaryCandidates.sort((left, right) => left - right).at(-1) || Math.max(0, Math.round(midpoint));
};

const isCtaItem = (item: TimelineItem) =>
  item.label?.startsWith('CTA:') || String(item.originId || '').startsWith(CTA_ORIGIN_PREFIX);

export function hasInjectedCta(items: TimelineItem[]): boolean {
  return items.some(isCtaItem);
}

export function removeCtaFromTimeline({
  tracks,
  items,
}: {
  tracks: TimelineTrack[];
  items: TimelineItem[];
}): { tracks: TimelineTrack[]; items: TimelineItem[] } {
  const ctaGroups = new Map<string, { start: number; end: number }>();
  items.filter(isCtaItem).forEach((item) => {
    const key = String(item.originId || item.id);
    const end = item.from + item.durationInFrames;
    const current = ctaGroups.get(key);
    if (!current) {
      ctaGroups.set(key, { start: item.from, end });
    } else {
      ctaGroups.set(key, {
        start: Math.min(current.start, item.from),
        end: Math.max(current.end, end),
      });
    }
  });

  const sortedGaps = Array.from(ctaGroups.values()).sort((left, right) => right.start - left.start);
  let filteredItems = items.filter((item) => !isCtaItem(item));

  sortedGaps.forEach((gap) => {
    const gapDuration = Math.max(0, gap.end - gap.start);
    filteredItems = filteredItems.map((item) => {
      if (item.from >= gap.start) {
        return {
          ...item,
          from: Math.max(0, item.from - gapDuration),
        };
      }
      return item;
    });
  });

  const filteredTracks = tracks
    .filter((track) => {
      if (track.id === CTA_BG_TRACK_ID || track.id === CTA_TEXT_TRACK_ID) {
        return filteredItems.some((item) => item.trackId === track.id);
      }
      return true;
    })
    .map((track) => ({
      ...track,
      items: (track.items || []).filter((item) => !isCtaItem(item as TimelineItem)),
    }));

  return {
    tracks: filteredTracks,
    items: filteredItems,
  };
}

export function injectCtaIntoTimeline({
  tracks,
  items,
  assets,
  ctaConfig,
  width,
  height,
  fps,
}: InsertArgs): { tracks: TimelineTrack[]; items: TimelineItem[]; assets: AssetEntry[] } {
  if (!ctaConfig?.enabled) return { tracks, items, assets };
  if (hasInjectedCta(items)) {
    return { tracks, items, assets };
  }

  const applyBranding = ctaConfig.applyBranding ?? true;
  const brand = applyBranding ? (ctaConfig.brandSnapshot || {}) : {};
  const preset = ctaConfig.presetSnapshot || {};
  const headline = ctaConfig.overrideHeadline || preset.headline || brand.defaultHeadline;
  const subtext = ctaConfig.overrideSubtext || preset.subtext || brand.defaultSubtext || '';
  const buttonLabel = ctaConfig.overrideButtonLabel || preset.buttonLabel || brand.defaultButtonLabel || '지금 확인하기';

  if (!headline) return { tracks, items, assets };

  const totalFrames = items.reduce((max, item) => Math.max(max, item.from + item.durationInFrames), 0);
  if (totalFrames <= 0) return { tracks, items, assets };

  const placement = ctaConfig.placement || preset.placement || 'end';
  const renderMode = (ctaConfig.renderMode || preset.renderMode || 'overlay') as 'scene' | 'overlay';
  const durationSec = Math.max(
    1,
    Math.min(
      6,
      Math.max(
        Number(preset.durationSec) || 1.5,
        Number(preset.ttsDurationSec) || 0
      )
    )
  );
  const durationInFrames = Math.max(fps, Math.round(durationSec * fps));
  const isVertical = height >= width;
  const bgTrack = ensureTrack(tracks, CTA_BG_TRACK_ID, 'CTA Background', 1);
  const textTrack = ensureTrack(tracks, CTA_TEXT_TRACK_ID, 'CTA Text', 0);
  const audioTrack = ensureTrack(tracks, CTA_AUDIO_TRACK_ID, 'Audio', 101);
  let workingItems = [...items];
  const insertStarts = (() => {
    const positions: number[] = [];

    if (placement === 'after_scene' && ctaConfig.insertAfterSceneId) {
      const anchorItem = workingItems.find((item) => item.id === ctaConfig.insertAfterSceneId);
      const afterSceneStart = anchorItem ? anchorItem.from + anchorItem.durationInFrames : workingItems.reduce((max, item) => Math.max(max, item.from + item.durationInFrames), 0);
      positions.push(afterSceneStart);
      workingItems = shiftItemsFromFrame(workingItems, afterSceneStart, durationInFrames);
      return positions;
    }

    if (placement === 'middle' || placement === 'both') {
      const middleStart = findMidInsertFrame(workingItems, totalFrames);
      positions.push(middleStart);
      workingItems = shiftItemsFromFrame(workingItems, middleStart, durationInFrames);
    }

    if (placement === 'end' || placement === 'both') {
      const endStart = workingItems.reduce((max, item) => Math.max(max, item.from + item.durationInFrames), 0);
      positions.push(endStart);
    }

    return positions;
  })();

  const primaryColor = preset.backgroundColor || (applyBranding ? brand.primaryColor : undefined) || '#111827';
  const secondaryColor = preset.backgroundColorSecondary || (applyBranding ? brand.secondaryColor : undefined) || primaryColor;
  const cardColor = renderMode === 'scene' ? 'rgba(15,23,42,0.72)' : 'rgba(15,23,42,0.82)';
  const showLogo = applyBranding ? (ctaConfig.showLogo ?? true) : false;
  const showHandle = applyBranding ? (ctaConfig.showHandle ?? true) : false;

  const backgroundImageUrl = isRenderableRemoteUrl(preset.backgroundImageUrl)
    ? preset.backgroundImageUrl
    : (applyBranding && isRenderableRemoteUrl(brand.backgroundImageUrl) ? brand.backgroundImageUrl : undefined);
  const logoUrl = applyBranding && isRenderableRemoteUrl(brand.logoUrl) ? brand.logoUrl : undefined;
  if (backgroundImageUrl) ensureAsset(assets, backgroundImageUrl, 'image');
  if (logoUrl) ensureAsset(assets, logoUrl, 'image');
  if (preset.ttsAudioUrl) ensureAsset(assets, preset.ttsAudioUrl, 'audio');

  insertStarts.forEach((startFrame, index) => {
    const visualPrefix = `CTA:${index + 1}`;
    const originId = `${CTA_ORIGIN_PREFIX}-${index + 1}`;
    const baseWidth = isVertical ? SHORTS_BASE_WIDTH : LONG_BASE_WIDTH;
    const baseHeight = isVertical ? SHORTS_BASE_HEIGHT : LONG_BASE_HEIGHT;
    const scenePanelRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 0, y: 0, width: 1080, height: 1920 }
        : { x: 0, y: 0, width: 1920, height: 1080 }
    );
    const overlayPanelRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 92, y: 1240, width: 896, height: 462 }
        : { x: 220, y: 700, width: 1480, height: 248 }
    );
    const logoRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 344, y: 260, width: 96, height: 96 }
        : { x: 890, y: 150, width: 140, height: 140 }
    );
    const overlayLogoRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 138, y: 1288, width: 84, height: 84 }
        : { x: 270, y: 748, width: 72, height: 72 }
    );
    const handleRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 0, y: 318, width: 788, height: 54 }
        : { x: 420, y: 300, width: 1080, height: 48 }
    );
    const overlayHandleRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 246, y: 1300, width: 560, height: 34 }
        : { x: 362, y: 762, width: 420, height: 28 }
    );
    const headlineRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 0, y: 393, width: 788, height: 157 }
        : { x: 280, y: 370, width: 1360, height: 140 }
    );
    const overlayHeadlineRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 138, y: 1374, width: 620, height: 116 }
        : { x: 270, y: 802, width: 860, height: 56 }
    );
    const subtextRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 0, y: 560, width: 788, height: 86 }
        : { x: 330, y: 520, width: 1260, height: 72 }
    );
    const overlaySubtextRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 138, y: 1498, width: 620, height: 60 }
        : { x: 270, y: 864, width: 840, height: 40 }
    );
    const buttonRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 340, y: 1180, width: 400, height: 96 }
        : { x: 760, y: 690, width: 400, height: 84 }
    );
    const overlayButtonRect = scaleTemplateRect(
      width,
      height,
      baseWidth,
      baseHeight,
      isVertical
        ? { x: 138, y: 1586, width: 264, height: 76 }
        : { x: 1186, y: 770, width: 286, height: 64 }
    );
    const panelRect = renderMode === 'scene' ? scenePanelRect : overlayPanelRect;
    const activeLogoRect = renderMode === 'scene' ? logoRect : overlayLogoRect;
    const activeHandleRect = renderMode === 'scene' ? handleRect : overlayHandleRect;
    const activeHeadlineRect = renderMode === 'scene' ? headlineRect : overlayHeadlineRect;
    const activeSubtextRect = renderMode === 'scene' ? subtextRect : overlaySubtextRect;
    const activeButtonRect = renderMode === 'scene' ? buttonRect : overlayButtonRect;
    const logoSize = activeLogoRect.width;
    const handleFontSize = renderMode === 'scene' ? (isVertical ? 22 : 20) : (isVertical ? 18 : 16);
    const headlineFontSize = renderMode === 'scene' ? (isVertical ? 52 : 50) : (isVertical ? 38 : 28);
    const subtextFontSize = renderMode === 'scene' ? 22 : (isVertical ? 18 : 15);
    const buttonFontSize = renderMode === 'scene' ? (isVertical ? 24 : 22) : (isVertical ? 20 : 16);

    if (backgroundImageUrl) {
      const bgImage: TimelineItem = {
        id: makeId('cta-bg-image'),
        type: 'image',
        originId,
        trackId: bgTrack.id,
        from: startFrame,
        durationInFrames,
        label: `${visualPrefix} Background`,
        src: backgroundImageUrl,
        mediaId: backgroundImageUrl,
        sourceWidth: panelRect.width,
        sourceHeight: panelRect.height,
        transform: {
          x: panelRect.x,
          y: panelRect.y,
          width: panelRect.width,
          height: panelRect.height,
          rotation: 0,
          opacity: 1,
          objectFit: 'cover',
          cornerRadius: renderMode === 'scene' ? 0 : (isVertical ? 32 : 24),
        },
      };
      items.push(bgImage);
      addTrackItem(tracks, bgImage);
    } else {
      const bgShape: TimelineItem = {
        id: makeId('cta-bg-shape'),
        type: 'shape',
        originId,
        trackId: bgTrack.id,
        from: startFrame,
        durationInFrames,
        label: `${visualPrefix} Background`,
        shapeType: 'rectangle',
        fillColor: renderMode === 'scene' ? primaryColor : cardColor,
        strokeColor: renderMode === 'scene' ? secondaryColor : 'rgba(255,255,255,0.08)',
        strokeWidth: renderMode === 'scene' ? 0 : 2,
        cornerRadius: renderMode === 'scene' ? 0 : (isVertical ? 32 : 24),
        transform: {
          x: panelRect.x,
          y: panelRect.y,
          width: panelRect.width,
          height: panelRect.height,
          rotation: 0,
          opacity: 1,
        },
      } as TimelineItem;
      items.push(bgShape);
      addTrackItem(tracks, bgShape);
    }

    if (showLogo && logoUrl) {
      const logoItem: TimelineItem = {
        id: makeId('cta-logo'),
        type: 'image',
        originId,
        trackId: textTrack.id,
        from: startFrame,
        durationInFrames,
        label: `${visualPrefix} Logo`,
        src: logoUrl,
        mediaId: logoUrl,
        transform: {
          x: activeLogoRect.x,
          y: activeLogoRect.y,
          width: activeLogoRect.width,
          height: activeLogoRect.height,
          rotation: 0,
          opacity: 1,
          objectFit: 'cover',
          cornerRadius: Math.round(activeLogoRect.width / 4),
        },
      };
      items.push(logoItem);
      addTrackItem(tracks, logoItem);
    }

    if (showHandle && (brand.channelHandle || brand.channelName)) {
      const handleItem: TimelineItem = {
        id: makeId('cta-handle'),
        type: 'text',
        originId,
        trackId: textTrack.id,
        from: startFrame,
        durationInFrames,
        label: `${visualPrefix} Handle`,
        text: brand.channelHandle || brand.channelName || '',
        fontSize: handleFontSize,
        fontFamily: 'Inter',
        fontWeight: 'bold',
        color: '#E2E8F0',
        textAlign: renderMode === 'scene' ? 'center' : 'left',
        verticalAlign: 'middle',
        backgroundColor: 'transparent',
        transform: {
          x: activeHandleRect.x,
          y: activeHandleRect.y,
          width: activeHandleRect.width,
          height: activeHandleRect.height,
          rotation: 0,
          opacity: 1,
        },
      };
      items.push(handleItem);
      addTrackItem(tracks, handleItem);
    }

    const headlineItem: TimelineItem = {
      id: makeId('cta-headline'),
      type: 'text',
      originId,
      trackId: textTrack.id,
      from: startFrame,
      durationInFrames,
      label: `${visualPrefix} Headline`,
      text: headline,
      fontSize: renderMode === 'scene' ? headlineFontSize : (isVertical ? 52 : 42),
      fontFamily: 'Inter',
      fontWeight: 'bold',
      color: '#FFFFFF',
      textAlign: renderMode === 'scene' ? 'center' : 'left',
      verticalAlign: 'middle',
      lineHeight: 1.1,
      backgroundColor: 'transparent',
      transform: {
        x: activeHeadlineRect.x,
        y: activeHeadlineRect.y,
        width: activeHeadlineRect.width,
        height: activeHeadlineRect.height,
        rotation: 0,
        opacity: 1,
      },
    };
    items.push(headlineItem);
    addTrackItem(tracks, headlineItem);

    if (subtext) {
      const subtextItem: TimelineItem = {
        id: makeId('cta-subtext'),
        type: 'text',
        originId,
        trackId: textTrack.id,
        from: startFrame,
        durationInFrames,
        label: `${visualPrefix} Subtext`,
        text: subtext,
        fontSize: subtextFontSize,
        fontFamily: 'Inter',
        fontWeight: 'medium',
        color: 'rgba(255,255,255,0.82)',
        textAlign: renderMode === 'scene' ? 'center' : 'left',
        verticalAlign: 'middle',
        lineHeight: 1.2,
        backgroundColor: 'transparent',
        transform: {
          x: activeSubtextRect.x,
          y: activeSubtextRect.y,
          width: activeSubtextRect.width,
          height: activeSubtextRect.height,
          rotation: 0,
          opacity: 1,
        },
      };
      items.push(subtextItem);
      addTrackItem(tracks, subtextItem);
    }

    if (buttonLabel) {
      const buttonBgItem: TimelineItem = {
        id: makeId('cta-button-bg'),
        type: 'shape',
        originId,
        trackId: bgTrack.id,
        from: startFrame,
        durationInFrames,
        label: `${visualPrefix} Button Background`,
        shapeType: 'rectangle',
        fillColor: '#FFFFFF',
        strokeColor: 'rgba(255,255,255,0.1)',
        strokeWidth: 0,
        cornerRadius: renderMode === 'scene' ? (isVertical ? 28 : 22) : (isVertical ? 24 : 20),
        transform: {
          x: activeButtonRect.x,
          y: activeButtonRect.y,
          width: activeButtonRect.width,
          height: activeButtonRect.height,
          rotation: 0,
          opacity: 1,
        },
      } as TimelineItem;
      items.push(buttonBgItem);
      addTrackItem(tracks, buttonBgItem);

      const buttonItem: TimelineItem = {
        id: makeId('cta-button'),
        type: 'text',
        originId,
        trackId: textTrack.id,
        from: startFrame,
        durationInFrames,
        label: `${visualPrefix} Button`,
        text: buttonLabel,
        fontSize: buttonFontSize,
        fontFamily: 'Inter',
        fontWeight: 'bold',
        color: '#0F172A',
        textAlign: 'center',
        verticalAlign: 'middle',
        backgroundColor: 'transparent',
        lineHeight: 1,
        transform: {
          x: activeButtonRect.x,
          y: activeButtonRect.y,
          width: activeButtonRect.width,
          height: activeButtonRect.height,
          rotation: 0,
          opacity: 1,
        },
      };
      items.push(buttonItem);
      addTrackItem(tracks, buttonItem);
    }

    if (preset.ttsAudioUrl) {
      const audioItem: TimelineItem = {
        id: makeId('cta-audio'),
        type: 'audio',
        originId,
        trackId: audioTrack.id,
        from: startFrame,
        durationInFrames,
        label: `${visualPrefix} Audio`,
        src: preset.ttsAudioUrl,
        mediaId: preset.ttsAudioUrl,
        sourceStart: 0,
        sourceEnd: durationInFrames,
        sourceDuration: durationInFrames,
        volume: 1,
      };
      items.push(audioItem);
      addTrackItem(tracks, audioItem);
    }
  });

  const finalItems = [...workingItems, ...items.filter(isCtaItem)].sort((left, right) => left.from - right.from);

  return { tracks: syncTrackItems([...tracks], finalItems), items: finalItems, assets: [...assets] };
}

export function upsertCtaIntoTimeline({
  tracks,
  items,
  assets,
  ctaConfig,
  width,
  height,
  fps,
}: InsertArgs): { tracks: TimelineTrack[]; items: TimelineItem[]; assets: AssetEntry[] } {
  const cleaned = removeCtaFromTimeline({ tracks, items });
  return injectCtaIntoTimeline({
    tracks: cleaned.tracks,
    items: cleaned.items,
    assets,
    ctaConfig,
    width,
    height,
    fps,
  });
}
